/**
 * @fileoverview Hook to retrieve top profitable products per period (TIME) for tooltips.
 * @module shared/hooks/dashboard/useTopProductsByPeriod
 */

import { useCallback, useContext, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FilterContext } from '@/shared/context/FilterContext'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import { statisticsService, type TimeAggregation } from '@/lib/statisticsService'
import { prefixWithOrg, queryKeys } from '@/shared/utils/queryKeys'
import { toDateOnlyStringLocal } from '@/shared/utils/dateUtils'
import { useLabelResolver } from '@/shared/hooks/useLabelResolver'

export interface PeriodProductRow {
  sku: string
  name: string
  profit: number
  quantity: number
}

export interface UseTopProductsByPeriodResult {
  /** Map of period -> sorted list of top profitable products (desc) */
  byPeriod: Map<string, PeriodProductRow[]>
  /** Helper to get up to N items for a given period */
  topFor: (period: string, limit?: number) => PeriodProductRow[]
  /** Helper to get up to N losing products for a given period */
  bottomFor: (period: string, limit?: number) => PeriodProductRow[]
  /** Helper to get total profit for a given period */
  totalProfitFor: (period: string) => number
  isLoading: boolean
  isError: boolean
  error: string | null
}

/**
 * Builds the filter and aggregation from the FilterContext to keep behavior aligned
 * with DashboardDataProvider.
 */
function useFilterParams(): { filter: Record<string, unknown>; timeAggregation: TimeAggregation } {
  const ctx = useContext(FilterContext)
  if (!ctx) throw new Error('useTopProductsByPeriod must be used within FilterProvider')

  const { startDate, endDate, aggregation, countries, skus, tags } = ctx
  const filter: Record<string, unknown> = {}
  if (startDate) filter.startDate = toDateOnlyStringLocal(startDate)
  if (endDate) filter.endDate = toDateOnlyStringLocal(endDate)
  if (countries.length > 0) filter.countries = countries
  if (skus.length > 0) filter.skus = skus
  if (tags.length > 0) filter.tags = tags

  let timeAggregation: TimeAggregation = 'DAILY'
  if (aggregation) {
    const map = {
      Daily: 'DAILY',
      Weekly: 'WEEKLY',
      Monthly: 'MONTHLY',
      Quarterly: 'QUARTERLY',
      Yearly: 'YEARLY',
    } as const
    timeAggregation = map[aggregation]
  }

  return { filter, timeAggregation }
}

/**
 * Fetches statistics grouped by TIME + PRODUCT and aggregates by period/product.
 */
export function useTopProductsByPeriod(): UseTopProductsByPeriodResult {
  const { productLabel } = useLabelResolver()
  const { activeOrgId } = useActiveOrg()
  const { filter, timeAggregation } = useFilterParams()

  const query = useQuery({
    queryKey: activeOrgId
      ? prefixWithOrg(
          activeOrgId as unknown as string,
          queryKeys.salesStatistics(filter, ['TIME', 'PRODUCT'], timeAggregation)
        )
      : queryKeys.salesStatistics(filter, ['TIME', 'PRODUCT'], timeAggregation),
    queryFn: () =>
      statisticsService.getSalesStatistics(filter, ['TIME', 'PRODUCT'], timeAggregation),
    enabled: Boolean(activeOrgId && filter && filter['startDate'] && filter['endDate']),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  const byPeriod = useMemo(() => {
    const map = new Map<string, PeriodProductRow[]>()
    const rows = query.data?.data || []

    rows.forEach(row => {
      const dims = row.dimensions as Record<string, unknown> | undefined
      const timeKey = (dims?.timePeriod as string) || ''
      const sku = (dims?.product as string) || ''
      if (!timeKey || !sku) return

      const m = row.metrics as Record<string, number | undefined> | undefined
      const revenue = (m?.sales as number) || 0
      const ad = (m?.advertising as number) || 0
      const cogs = (m?.cogs as number) || 0
      const direct = (m?.directCosts as number) || 0
      const cm2 = typeof m?.cm2 === 'number' ? (m?.cm2 as number) : revenue - (ad + cogs + direct)
      const qty = (m?.quantity as number) || 0

      const list = map.get(timeKey) || []
      const existingIndex = list.findIndex(p => p.sku === sku)
      const existing = existingIndex >= 0 ? list[existingIndex] : undefined
      if (existing) {
        existing.profit += cm2
        existing.quantity += qty
      } else {
        list.push({ sku, name: productLabel(sku), profit: cm2, quantity: qty })
      }
      map.set(timeKey, list)
    })

    // Sort each period by profit descending
    for (const [, list] of map.entries()) {
      list.sort((a, b) => b.profit - a.profit)
    }

    return map
  }, [query.data, productLabel])

  const topFor = useCallback(
    (period: string, limit = 3): PeriodProductRow[] => {
      const list = byPeriod.get(period) || []
      return list.filter(p => p.profit > 0).slice(0, limit)
    },
    [byPeriod]
  )

  const bottomFor = useCallback(
    (period: string, limit = 3): PeriodProductRow[] => {
      const list = byPeriod.get(period) || []
      return list
        .filter(p => p.profit < 0)
        .sort((a, b) => a.profit - b.profit) // Sort ascending (most negative first)
        .slice(0, limit)
    },
    [byPeriod]
  )

  const totalProfitFor = useCallback(
    (period: string): number => {
      const list = byPeriod.get(period) || []
      return list.reduce((sum, p) => sum + p.profit, 0)
    },
    [byPeriod]
  )

  return {
    byPeriod,
    topFor,
    bottomFor,
    totalProfitFor,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error?.message || null,
  }
}
